/**
 * Quest 配置块 JSON Schema 定义
 */
export const questSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/quest.json',
  title: 'Quest 配置',
  description: 'Quest（任务）配置数据结构',
  type: 'object',
  required: ['quest_id', 'name', 'quest_type'],
  properties: {
    quest_id: {
      type: 'string',
      title: 'Quest ID',
      description: 'Quest 唯一标识符',
      pattern: '^quest_[a-zA-Z0-9_]+$',
      minLength: 7,
      maxLength: 50,
      examples: ['quest_main_01', 'quest_side_investigate'],
    },
    name: {
      type: 'string',
      title: '任务名称',
      description: '任务在游戏中显示的名称',
      minLength: 1,
      maxLength: 100,
      examples: ['寻找失踪的商人', '调查神秘事件', '收集证据'],
    },
    description: {
      type: 'string',
      title: '任务描述',
      description: '任务的详细描述',
      minLength: 0,
      maxLength: 1000,
    },
    quest_type: {
      type: 'string',
      title: '任务类型',
      description: '任务的类型分类',
      enum: ['主线任务', '支线任务', '隐藏任务', '日常任务', '活动任务'],
      default: '支线任务',
    },
    difficulty: {
      type: 'string',
      title: '难度',
      description: '任务难度等级',
      enum: ['简单', '普通', '困难', '极难'],
      default: '普通',
    },
    objectives: {
      type: 'array',
      title: '任务目标',
      description: '任务需要完成的目标列表',
      items: {
        type: 'object',
        required: ['objective_id', 'description'],
        properties: {
          objective_id: {
            type: 'string',
            title: '目标 ID',
            pattern: '^obj_[a-zA-Z0-9_]+$',
          },
          description: {
            type: 'string',
            title: '目标描述',
            minLength: 1,
            maxLength: 200,
          },
          objective_type: {
            type: 'string',
            title: '目标类型',
            enum: ['收集', '对话', '击败', '探索', '护送', '调查'],
          },
          target: {
            type: 'string',
            title: '目标对象',
            description: '目标对象的 ID（NPC ID, Item ID 等）',
          },
          quantity: {
            type: 'integer',
            title: '数量',
            minimum: 1,
            default: 1,
          },
          optional: {
            type: 'boolean',
            title: '是否可选',
            default: false,
          },
          completion_condition: {
            type: 'string',
            title: '完成条件',
            description: '目标完成的条件表达式',
          },
        },
      },
      minItems: 1,
      maxItems: 20,
    },
    prerequisites: {
      type: 'object',
      title: '前置条件',
      description: '接取任务的前置条件',
      properties: {
        level: {
          type: 'integer',
          title: '最低等级',
          minimum: 1,
        },
        completed_quests: {
          type: 'array',
          title: '前置任务',
          description: '必须完成的前置任务列表',
          items: {
            type: 'string',
            pattern: '^quest_[a-zA-Z0-9_]+$',
          },
        },
        required_items: {
          type: 'array',
          title: '需要物品',
          description: '接取任务所需的物品',
          items: {
            type: 'object',
            properties: {
              item_id: {
                type: 'string',
                pattern: '^item_[a-zA-Z0-9_]+$',
              },
              quantity: {
                type: 'integer',
                minimum: 1,
              },
            },
          },
        },
        variables: {
          type: 'object',
          title: '变量条件',
          description: '需要满足的变量条件',
          additionalProperties: {
            type: ['string', 'number', 'boolean'],
          },
        },
      },
    },
    rewards: {
      type: 'object',
      title: '任务奖励',
      description: '完成任务后的奖励',
      properties: {
        experience: {
          type: 'integer',
          title: '经验值',
          minimum: 0,
          default: 0,
        },
        gold: {
          type: 'integer',
          title: '金币',
          minimum: 0,
          default: 0,
        },
        items: {
          type: 'array',
          title: '物品奖励',
          items: {
            type: 'object',
            required: ['item_id', 'quantity'],
            properties: {
              item_id: {
                type: 'string',
                pattern: '^item_[a-zA-Z0-9_]+$',
              },
              quantity: {
                type: 'integer',
                minimum: 1,
              },
            },
          },
        },
        reputation: {
          type: 'object',
          title: '声望奖励',
          description: '各派系的声望变化',
          additionalProperties: {
            type: 'integer',
          },
        },
      },
    },
    time_limit: {
      type: 'integer',
      title: '时间限制',
      description: '任务时间限制（秒）',
      minimum: 0,
    },
    failure_conditions: {
      type: 'array',
      title: '失败条件',
      description: '任务失败的条件列表',
      items: {
        type: 'object',
        properties: {
          condition: {
            type: 'string',
            title: '失败条件表达式',
          },
          description: {
            type: 'string',
            title: '失败原因描述',
          },
        },
      },
    },
    quest_giver: {
      type: 'string',
      title: '任务发布者',
      description: '发布任务的 NPC ID',
      pattern: '^npc_[a-zA-Z0-9_]+$',
    },
    auto_accept: {
      type: 'boolean',
      title: '自动接取',
      description: '是否在满足条件时自动接取',
      default: false,
    },
    repeatable: {
      type: 'boolean',
      title: '可重复',
      description: '是否可以重复完成',
      default: false,
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type QuestConfig = {
  quest_id: string
  name: string
  description?: string
  quest_type: string
  difficulty?: string
  objectives: Array<{
    objective_id: string
    description: string
    objective_type?: string
    target?: string
    quantity?: number
    optional?: boolean
    completion_condition?: string
  }>
  prerequisites?: {
    level?: number
    completed_quests?: string[]
    required_items?: Array<{
      item_id: string
      quantity: number
    }>
    variables?: Record<string, string | number | boolean>
  }
  rewards?: {
    experience?: number
    gold?: number
    items?: Array<{
      item_id: string
      quantity: number
    }>
    reputation?: Record<string, number>
  }
  time_limit?: number
  failure_conditions?: Array<{
    condition: string
    description: string
  }>
  quest_giver?: string
  auto_accept?: boolean
  repeatable?: boolean
  metadata?: Record<string, any>
}
